import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Sparkles, ArrowRight, ShieldCheck } from "lucide-react";

const TIERS = [
  {
    name: "Starter",
    description: "Ideal for single-location cafés looking to stop guessing daily demand and cut waste.",
    monthlyPrice: 99,
    annualPrice: 79,
    popular: false,
    trialDays: 14,
    badge: null,
    features: [
      "1 café location",
      "Daily product-level demand forecasting",
      "7-day weather impact intelligence",
      "Basic ingredient & stock tracking",
      "Daily sales accuracy tracker (MAPE)",
      "Email & CSV export support",
      "1-Click CSV/Excel sales history import",
    ],
  },
  {
    name: "Pro",
    description: "For busy, high-volume cafés that want automated purchasing and AI operations guidance.",
    monthlyPrice: 199,
    annualPrice: 159,
    popular: true,
    trialDays: 14,
    badge: "Most Popular · Best Value",
    features: [
      "Everything in Starter, plus:",
      "Automated Purchasing & 1-Click POs",
      "Real-time Lightspeed & Square POS sync",
      "Generative AI daily operations briefing",
      "Advanced waste tracking & cost analytics",
      "Local events & public holiday integration",
      "Recipe ingredient depletion modeling",
      "Priority phone & email support",
    ],
  },
  {
    name: "Multi-site",
    description: "For multi-location café groups and roasteries needing central purchasing and org oversight.",
    monthlyPrice: 499,
    annualPrice: 399,
    popular: false,
    trialDays: 14,
    badge: "Enterprise Multi-Location",
    features: [
      "Everything in Pro, plus:",
      "Up to 5 locations (add more anytime)",
      "Group overview & central purchasing",
      "Cross-location inventory balancing",
      "Role-based staff permissions",
      "Dedicated account onboarding manager",
      "Custom POS & ERP integrations",
      "Service Level Agreement (99.9% uptime)",
    ],
  },
];

// Annual billing toggle is intentionally disabled until Stripe annual prices
// exist (lookup keys pro_annual etc.). TIERS keep annualPrice so the toggle can
// be restored by re-adding the switcher and `const [isAnnual, setIsAnnual] = useState(true)`.
const ANNUAL_ENABLED = true;

export default function Pricing() {
  const [isAnnual, setIsAnnual] = useState(true);
  const showSwitcher = ANNUAL_ENABLED;

  return (
    <section id="pricing" className="bg-white border-y border-zinc-200/80 scroll-mt-16 py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mx-auto"
        >
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-3.5 py-1.5 rounded-full">
            <Sparkles className="h-3.5 w-3.5" /> Transparent Pricing
          </div>
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-zinc-900 mt-4">
            Predictable plans that pay for themselves
          </h2>
          <p className="text-zinc-500 mt-3 text-base">
            Start with a 14-day free trial. Credit card required — you won't be charged until the trial ends. Cancel anytime.
          </p>

          {/* Billing Switcher — re-enable alongside ANNUAL_ENABLED */}
          {showSwitcher && (
            <div className="mt-8 inline-flex items-center bg-zinc-100 p-1.5 rounded-full border border-zinc-200">
              <button
                onClick={() => setIsAnnual(false)}
                className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-all duration-200 ${
                  !isAnnual
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "text-zinc-500 hover:text-zinc-800"
                }`}
              >
                Monthly Billing
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-full transition-all duration-200 ${
                  isAnnual
                    ? "bg-zinc-900 text-white shadow-sm"
                    : "text-zinc-500 hover:text-zinc-800"
                }`}
              >
                <span>Annual Billing</span>
                <span className="bg-emerald-500 text-zinc-950 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  Save 20%
                </span>
              </button>
            </div>
          )}
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-14 items-stretch">
          {TIERS.map((t, i) => {
            const price = isAnnual ? t.annualPrice : t.monthlyPrice;
            return (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                className={`rounded-2xl border bg-white p-8 shadow-card flex flex-col justify-between relative transition-all duration-200 ${
                  t.popular
                    ? "border-zinc-900 ring-2 ring-zinc-900 shadow-xl md:-mt-3 md:mb-3"
                    : "border-zinc-200 hover:border-zinc-300"
                }`}
              >
                {t.badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-zinc-900 text-amber-400 border border-zinc-700 text-[11px] font-bold px-3.5 py-1 rounded-full uppercase tracking-wider shadow-md whitespace-nowrap">
                    {t.badge}
                  </div>
                )}

                <div>
                  <div className="font-display font-semibold text-xl text-zinc-900">{t.name}</div>
                  <p className="text-xs text-zinc-500 mt-2 min-h-[32px] leading-relaxed">
                    {t.description}
                  </p>

                  <div className="mt-6 pb-6 border-b border-zinc-100">
                    <div className="flex items-baseline gap-1">
                      <span className="text-xs font-semibold text-zinc-500">NZD</span>
                      <span className="font-display text-4xl md:text-5xl font-bold text-zinc-900 tnum">
                        ${price}
                      </span>
                      <span className="text-zinc-500 text-sm">/month</span>
                    </div>
                    <div className="text-[11px] text-zinc-400 mt-1">
                      {isAnnual && showSwitcher ? `Billed annually (NZ$${price * 12}/yr)` : "Billed monthly, cancel anytime"}
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-3">
                      Included in {t.name}:
                    </div>
                    <ul className="space-y-3 text-xs">
                      {t.features.map((f) => (
                        <li key={f} className="flex items-start gap-2.5 text-zinc-700">
                          <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                          <span className="leading-snug">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="pt-8 mt-6 border-t border-zinc-100">
                  <Link
                    to="/login"
                    className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all duration-200 ${
                      t.popular
                        ? "bg-zinc-900 hover:bg-zinc-800 text-white shadow-lg shadow-zinc-900/20"
                        : "border border-zinc-300 text-zinc-900 hover:bg-zinc-50"
                    }`}
                  >
                    Start 14-Day Free Trial <ArrowRight className="h-4 w-4" />
                  </Link>
                  <div className="text-center text-[11px] text-zinc-400 mt-2 flex items-center justify-center gap-1">
                    <ShieldCheck className="h-3.5 w-3.5 text-zinc-400" /> 14-day free trial · card required · cancel anytime
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
